/**
 * features/setup/SetupDashboard.tsx
 * ------------------------------------------------------------------
 * AUDIT M6 — a setup landing/progress hub (low impact, convenience).
 * One screen that shows each setup area, its record count, whether
 * it still needs attention, and a button straight into it — so a
 * new admin has an obvious "start here" order instead of guessing
 * from the nav. Mirrors Al-Nur's SetupDashboard; read-only summary,
 * no new data logic.
 * ------------------------------------------------------------------
 */
import {
  Users,
  GraduationCap,
  BookOpen,
  ClipboardList,
  ArrowRight,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import { useData } from '@/contexts/DataContext';
import type { TabId } from '@/components/Shell';
import { PageHeader, Card, Spinner } from '@/components/ui/primitives';

export default function SetupDashboard({
  onNavigate,
}: {
  onNavigate: (t: TabId) => void;
}) {
  const { teachers, students, subjects, classes, loading } =
    useData();

  if (loading) return <Spinner label="Loading setup…" />;

  // Sensible setup order: subjects & classes first (students and
  // teachers reference them), then teachers, then students.
  const items: {
    tab: TabId;
    label: string;
    icon: typeof Users;
    count: number;
    hint: string;
  }[] = [
    {
      tab: 'subjects',
      label: 'Subjects',
      icon: BookOpen,
      count: subjects.length,
      hint: 'Define the exam subjects first.',
    },
    {
      tab: 'classes',
      label: 'Classes',
      icon: ClipboardList,
      count: classes.length,
      hint: 'Create the form classes (e.g. Form 5A).',
    },
    {
      tab: 'teachers',
      label: 'Teachers',
      icon: Users,
      count: teachers.length,
      hint: 'Add teachers and their subject/class assignments.',
    },
    {
      tab: 'students',
      label: 'Students',
      icon: GraduationCap,
      count: students.length,
      hint: 'Register students into their classes.',
    },
  ];

  const done = items.filter((i) => i.count > 0).length;

  return (
    <div>
      <PageHeader
        title="Setup"
        subtitle="Get the school data in place before entering marks"
      />

      <Card className="p-5 mb-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-bold text-gray-800 dark:text-white">
              Setup progress
            </p>
            <p className="text-sm text-gray-500">
              {done} of {items.length} areas have data
            </p>
          </div>
          <div className="text-3xl font-bold text-primary">
            {Math.round((done / items.length) * 100)}%
          </div>
        </div>
        <div className="mt-3 h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#2d5295] to-[#d4a92c] transition-all"
            style={{ width: `${(done / items.length) * 100}%` }}
          />
        </div>
      </Card>

      <div className="grid sm:grid-cols-2 gap-4">
        {items.map((it) => {
          const Icon = it.icon;
          const empty = it.count === 0;
          return (
            <Card
              key={it.tab}
              className="p-5 flex flex-col gap-3 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                  <Icon size={20} />
                </div>
                {empty ? (
                  <span className="flex items-center gap-1 text-xs font-semibold text-amber-600">
                    <AlertCircle size={13} /> Needs setup
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-xs font-semibold text-emerald-600">
                    <CheckCircle2 size={13} /> {it.count} added
                  </span>
                )}
              </div>
              <div>
                <p className="font-bold text-gray-800 dark:text-white">
                  {it.label}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {it.hint}
                </p>
              </div>
              <button
                onClick={() => onNavigate(it.tab)}
                className="mt-auto flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-[#2d5295] to-[#1b3a6b] text-white font-semibold text-sm hover:scale-[1.02] transition-transform"
              >
                {empty ? 'Set up' : 'Manage'} {it.label}
                <ArrowRight size={15} />
              </button>
            </Card>
          );
        })}
      </div>

      <p className="text-xs text-gray-400 mt-4">
        Tip: do these in order — subjects and classes first, since
        teachers and students reference them.
      </p>
    </div>
  );
}
